import React, { useEffect } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { BarChart, LineChart, PieChart, AreaChart } from './charts';
import ModuleConfig from './ModuleConfig';
import ThemeSelector from './ThemeSelector';

const Dashboard = () => {
  const { theme, themes, modules } = useTheme();

  useEffect(() => {
    document.title = 'Sydney Ferris - Dashboard';
  }, []);

  return (
    <div className="min-h-screen theme-bg-secondary">
      <div className="max-w-7xl mx-auto px-4 py-6 sm:py-8">
        {/* Header */}
        <div className="theme-bg rounded-lg shadow-md p-4 sm:p-6 mb-6 sm:mb-8">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold theme-primary">Dynamic Dashboard</h1>
              <p className="theme-secondary mt-1 text-sm sm:text-base">
                Customize your theme and modules for a personalized experience
              </p>
            </div>
            <ThemeSelector />
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-8">
          {/* Module Configuration */}
          <div className="lg:col-span-1">
            <ModuleConfig />
          </div>

          {/* Charts Display */}
          <div className="lg:col-span-1 space-y-4 sm:space-y-6">
            {modules.charts?.enabled && (
              <>
                <div className="theme-bg rounded-lg shadow-md p-4 sm:p-6">
                  <h2 className="text-lg sm:text-xl font-semibold theme-primary mb-4">Charts Module</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <BarChart moduleId="charts" dataIndex={0} />
                    <LineChart moduleId="charts" dataIndex={1} />
                    <PieChart moduleId="charts" dataIndex={2} />
                  </div>
                </div>

                <div className="theme-bg rounded-lg shadow-md p-4 sm:p-6">
                  <h2 className="text-lg sm:text-xl font-semibold theme-primary mb-4">Analytics Module</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <BarChart moduleId="analytics" dataIndex={0} />
                    <LineChart moduleId="analytics" dataIndex={1} />
                  </div>
                </div>
              </>
            )}

            {modules.analytics?.enabled && (
              <div className="theme-bg rounded-lg shadow-md p-4 sm:p-6">
                <h2 className="text-lg sm:text-xl font-semibold theme-primary mb-4">User Activity</h2>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-3 sm:p-4 theme-bg-secondary rounded-lg">
                    <span className="text-xs sm:text-sm font-medium text-gray-700">Active Users</span>
                    <span className="text-xl sm:text-2xl font-bold text-primary-600">
                      {modules.analytics?.data?.[0]?.value || 0}
                    </span>
                  </div>
                  <div className="flex items-center justify-between p-3 sm:p-4 theme-bg-secondary rounded-lg">
                    <span className="text-xs sm:text-sm font-medium text-gray-700">Conversion Rate</span>
                    <span className="text-xl sm:text-2xl font-bold text-secondary-600">
                      {modules.analytics?.data?.[1]?.value || 0}%
                    </span>
                  </div>
                </div>
              </div>
            )}

            {modules.users?.enabled && (
              <div className="theme-bg rounded-lg shadow-md p-4 sm:p-6">
                <h2 className="text-lg sm:text-xl font-semibold theme-primary mb-4">Users Module</h2>
                <div className="grid grid-cols-2 gap-3 sm:gap-4">
                  {(modules.users?.data || []).slice(0, 4).map((userId, index) => (
                    <div key={index} className="text-center p-3 sm:p-4 theme-bg-secondary rounded-lg">
                      <div className="text-2xl sm:text-3xl font-bold text-primary-600 mb-2">
                        👤
                      </div>
                      <div className="text-xs sm:text-sm theme-secondary">
                        User {userId}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Theme Info */}
        <div className="mt-6 sm:mt-8 theme-bg rounded-lg shadow-md p-4 sm:p-6">
          <h3 className="text-base sm:text-lg font-semibold theme-primary mb-4">Current Theme</h3>
          <div className="flex items-center space-x-3 sm:space-x-4">
            <div
              className="w-8 h-8 rounded-full border-2"
              style={{
                backgroundColor: themes[theme]?.primary,
                borderColor: themes[theme]?.border
              }}
            />
            <div>
              <h4 className="font-medium theme-primary capitalize text-sm sm:text-base">{theme}</h4>
              <p className="text-xs sm:text-sm theme-secondary">
                {themes[theme]?.primary && (
                  <span className="inline-block w-3 h-3 rounded-full mr-2 align-middle"
                        style={{ backgroundColor: themes[theme]?.primary }} />
                )}
                Applied across all components
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
